// Per-user feature flags (#327). A flag's DEFAULT is what signed-out users
// (and any profile without an override) get — signed-out behaviour never
// changes when a flag ships. An activated pilot profile's `flags` jsonb
// (loaded by session.ts) overrides per key; non-boolean values are ignored,
// because a typo'd dashboard edit must degrade to the default, never crash
// a gate. Flipping a flag on the user's row changes the app on next load —
// no deploy. The same claim later carries tier entitlements (#241), so
// nothing here is throwaway.
import { getAuthState, useAuth } from './session';

export const FLAG_DEFS = {
  /**
   * Reharmonisation: the Harmony panel's Reharmonize levers, the chord
   * editor's Substitutes list, and the reharm engine commands. Default ON.
   * A profile's {"reharm": false} turns it off: stamped by the tuesday-class
   * codes minted by SQL (invite_codes.grant_flags, migration 0003), never by
   * the admin page, which stopped setting flags in Oct 2026. Layer CRUD stays
   * available either way: layers are score data, the flag gates the ENGINE.
   */
  reharm: { default: true },
} as const;

export type FlagId = keyof typeof FLAG_DEFS;

export function flagEnabled(id: FlagId): boolean {
  const value = getAuthState().flags[id];
  return typeof value === 'boolean' ? value : FLAG_DEFS[id].default;
}

/** Reactive read for components; re-renders when auth (and its flags) move. */
export function useFlag(id: FlagId): boolean {
  useAuth();
  return flagEnabled(id);
}
